import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, Link, Loading, Modal, Text, Icon } from '@maui/elements'
import { HashSummary } from '@horizon-shared/hash-details'
import { useAuthProvider } from '@maui-mf/common-react'
import IndicatorDetails from '../details/IndicatorDetails'
import { encodeURIComponentTwice, getVirusTotalIndicatorLink } from '../../utilities'
import { maApiFetch } from '../../api/apis'

const typeDisplayMap = {
  fqdn: 'FQDN',
  ipv4: 'IPV4',
  md5: 'Hash',
  sha1: 'Hash',
  sha256: 'Hash',
  url: 'URL',
}
const valueDisplay = (item) => {
  const hashes = item.associated_hashes
  // Default to SHA256 value if available, otherwise one of the other hashes
  // ordered SHA256 > MD5 > SHA1 > indicator value
  if (typeDisplayMap[item.type] === 'Hash') {
    const sha256Match = hashes?.find(hash => hash.type === 'sha256')
    const md5Match = hashes?.find(hash => hash.type === 'md5')
    const sha1Match = hashes?.find(hash => hash.type === 'sha1')
    return sha256Match?.value || md5Match?.value || sha1Match?.value || item.value
  }
  return item.value
}
function useFetchIndicatorData(type, value) {
  const [indicator, setIndicator] = React.useState(undefined)
  useEffect(() => {
    async function fetchIndicator({ type, value }) {
      const params = ['explainer=true']
      const path = `/api/v4/indicator/${type}${value === undefined ? '' : `/${encodeURIComponent(value)}`}?${params.join('&')}`

      try {
        const data = await maApiFetch(path)
        setIndicator(data)
      } catch {
        setIndicator(null)
      }
    }
    fetchIndicator({ type, value })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, value])

  return indicator
}

const IndicatorModalBase = ({ noDataFallback, ...modalProps }) => {
  const { name: type, id: value } = modalProps
  const indicator = useFetchIndicatorData(type, value)

  const loading = indicator === undefined
  const notFound = indicator === null

  const isHashIndicator = type?.match(/md5|sha1|sha256/i)

  const link = useMemo(() => {
    if (!indicator) {
      return `/indicator/${type}${value ? `/${encodeURIComponentTwice(value)}` : ''}`
    }

    // encoding because the value can be a url here
    return `/indicator/${indicator.type}/${encodeURIComponentTwice(indicator.value)}`
  }, [indicator, type, value])

  const getSize = () => {
    if (loading) {
      return 'small'
    }

    if (!isHashIndicator && notFound) {
      return 'small'
    }

    return 'large'
  }

  const modalTitle = useMemo(() => {
    if (!indicator) {
      return null
    }

    return valueDisplay(indicator)
  }, [indicator])

  const getModalContent = () => {
    if (isHashIndicator && indicator !== undefined) {
      return (
        <Box marginY="16px">
          <HashSummary indicator={indicator} hash={value} />
        </Box>
      )
    }

    if (loading) {
      return <Loading />
    }

    if (notFound) {
      return <Box marginTop="16px">{noDataFallback || 'No data available'}</Box>
    }

    return <IndicatorDetails indicator={indicator} summaryOnly />
  }

  return (
    <Modal {...modalProps} size={getSize()}>
      <Modal.Header onClose={modalProps.onClose}>
        <Icon name='fingerprint' size="xl" verticalAlign="middle" />
        <Modal.Title>
          <Box as="span" wordBreak="break-word">
            {modalTitle}
          </Box>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Text>Indicator Summary</Text>
          {link && (
            <Box display="flex" alignItems="flex-start" gap="spaceGapSmall">
              <Link href={link} alt="View more details">
                View More Details
              </Link>
              <span>  |  </span>
              <Link href={link} alt="View more details" target="_blank" rel="noopener noreferrer">
                <Icon name="external-link"></Icon>
              </Link>
            </Box>
          )}
        </Box>

        {getModalContent()}
      </Modal.Body>
    </Modal>
  )
}

IndicatorModalBase.propTypes = {
  noDataFallback: PropTypes.oneOfType([PropTypes.node, PropTypes.string])
}

const IndicatorModal = props => {
  const { isGTI } = useAuthProvider()
  const { name: type, id: value } = props

  const redirect = useMemo(() => {
    return isGTI && getVirusTotalIndicatorLink(type, value)
  }, [isGTI, type, value])

  useEffect(() => {
    if (redirect) {
      window.location.href = redirect
    }
  }, [redirect])

  return redirect ? null : <IndicatorModalBase {...props} />
}

export default IndicatorModal
